export type MedicalRecordStep = 
  | 'identification'
  | 'complaint'
  | 'anamnese'
  | 'physical-exam'
  | 'diagnoses'
  | 'prescriptions'
  | 'exams'
  | 'evolution'
  | 'summary';

interface StepConfig {
  id: MedicalRecordStep;
  label: string;
  shortLabel: string;
}

const steps: StepConfig[] = [
  { id: 'identification', label: 'Identificação', shortLabel: 'ID' },
  { id: 'complaint', label: 'Queixa Principal', shortLabel: 'QP' },
  { id: 'anamnese', label: 'Anamnese', shortLabel: 'AN' },
  { id: 'physical-exam', label: 'Exame Físico', shortLabel: 'EF' },
  { id: 'diagnoses', label: 'Diagnósticos', shortLabel: 'DX' },
  { id: 'prescriptions', label: 'Prescrições', shortLabel: 'RX' },
  { id: 'exams', label: 'Solicitação de Exames', shortLabel: 'EX' },
  { id: 'evolution', label: 'Evolução', shortLabel: 'EV' },
  { id: 'summary', label: 'Resumo', shortLabel: 'RF' },
];

interface MedicalRecordStepperProps {
  currentStep: MedicalRecordStep;
  onStepChange: (step: MedicalRecordStep) => void;
  completedSteps?: MedicalRecordStep[];
  disabled?: boolean;
}

export function MedicalRecordStepper({
  currentStep,
  onStepChange,
  completedSteps = [],
  disabled = false,
}: MedicalRecordStepperProps) {
  const currentIndex = steps.findIndex(s => s.id === currentStep);

  const goToStep = (step: MedicalRecordStep) => {
    if (!disabled) {
      onStepChange(step);
    }
  };

  const goNext = () => {
    if (currentIndex < steps.length - 1) {
      onStepChange(steps[currentIndex + 1]!.id);
    }
  };

  const goPrevious = () => {
    if (currentIndex > 0) {
      onStepChange(steps[currentIndex - 1]!.id);
    }
  };

  const isStepCompleted = (stepId: MedicalRecordStep) => completedSteps.includes(stepId);
  const isStepAccessible = (stepIndex: number) => {
    if (disabled) return false;
    if (stepIndex === 0) return true;
    const prevStep = steps[stepIndex - 1];
    return prevStep && isStepCompleted(prevStep.id);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Prontuário</h3>
        <span className="text-sm text-gray-500">
          Etapa {currentIndex + 1} de {steps.length}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={goPrevious}
          disabled={currentIndex === 0 || disabled}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex-1 flex items-center justify-center gap-1">
          {steps.map((step, index) => {
            const isCompleted = isStepCompleted(step.id);
            const isCurrent = step.id === currentStep;
            const isAccessible = isStepAccessible(index);

            return (
              <div key={step.id} className="flex items-center">
                <button
                  type="button"
                  onClick={() => goToStep(step.id)}
                  disabled={!isAccessible}
                  className={`
                    flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                    transition-all
                    ${isCurrent
                      ? 'bg-primary-600 text-white ring-2 ring-primary-300'
                      : isCompleted
                        ? 'bg-green-500 text-white'
                        : isAccessible
                          ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }
                  `}
                  title={step.label}
                >
                  {isCompleted && !isCurrent ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </button>
                {index < steps.length - 1 && (
                  <div
                    className={`w-4 h-0.5 mx-0.5 ${
                      isStepCompleted(step.id) ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={goNext}
          disabled={currentIndex === steps.length - 1 || disabled}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="mt-4 text-center">
        <span className="text-sm font-medium text-primary-600">
          {steps[currentIndex]?.label}
        </span>
      </div>
    </div>
  );
}

export { steps };
